/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, makeObservable, observable } from 'mobx';

import type { NotificationProps } from '@veeam/components/lib/Grid/types';

import { DEFAULT_AUTO_UPDATE_INTERVAL } from '../../../../constants';
import { refreshNotificationRenderer } from '../../../components/NotificationRenderer';
import { isErrorThatNeedToBeShown } from '../../../../../core';

import type { GridProps } from '../../../interfaces';
import type { GridStore } from '../grid-store';

export class NotificationStore<RowDataT, TotalRowDataT, FilterT> {
    @observable isOutdated = false;

    autoUpdateLoading = false;
    autoUpdateInterval: number;
    intervalId: number;

    constructor(
        private gridStore: GridStore<RowDataT, TotalRowDataT, FilterT>,
        props: GridProps<RowDataT, TotalRowDataT, FilterT>
    ) {
        makeObservable(this);

        this.autoUpdateInterval = props.autoUpdateInterval ?? DEFAULT_AUTO_UPDATE_INTERVAL;
    }

    get notificationConfig(): NotificationProps {
        return {
            isShown: this.isOutdated,
            renderContent: () =>
                refreshNotificationRenderer({
                    onRefresh: this.gridStore.reloadGrid,
                    onIgnore: this.ignoreOutdatedDataMessage,
                }),
            onRequestClose: this.closeOutdatedDataMessage,
        };
    }

    @action.bound
    subscribeToUpdates(): void {
        this.intervalId = window.setInterval(() => {
            if (this.isOutdated === false && this.gridStore.loading === false && this.autoUpdateLoading === false) {
                this.setAutoUpdateLoading(true);

                this.gridStore.requestStore.sendRequest(true)
                    .then(this.gridStore.sortingStore.doLocalSortingIfRequired)
                    .then(this.gridStore.requestStore.injectIdsIfRequired)
                    .then(({ data, meta }) => {
                        if (this.gridStore.loading) return;

                        const selected = [...this.gridStore.selected];

                        const newItemsIds = new Set(data.map(item => item[this.gridStore.selection.field]));

                        const selectedItemsHaveChanged = !selected.every(item => newItemsIds.has(item[this.gridStore.selection.field]));

                        if (selectedItemsHaveChanged) {
                            this.showOutdatedDataMessage();
                        } else {
                            this.gridStore.selectionStore.needToBeSelected = selected;
                            this.gridStore.updateData(data, meta.pagingInfo.total);
                        }
                    })
                    .catch((err) => {
                        if (isErrorThatNeedToBeShown(err)) {
                            console.error(err);
                        }
                    })
                    .finally(() => {
                        this.setAutoUpdateLoading(false);
                    });
            }
        }, this.autoUpdateInterval);
    }

    @action.bound
    unsubscribeFromUpdates(): void {
        clearInterval(this.intervalId);
    }

    @action.bound
    ignoreOutdatedDataMessage(): void {
        this.closeOutdatedDataMessage();
        this.unsubscribeFromUpdates();
    }

    @action.bound
    showOutdatedDataMessage(): void {
        this.isOutdated = true;
    }

    @action.bound
    closeOutdatedDataMessage(): void {
        this.isOutdated = false;
    }

    setAutoUpdateLoading(autoUpdateLoading: boolean): void {
        this.autoUpdateLoading = autoUpdateLoading;
    }
}
